import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { IOrderConfirmationModel, IShippingType } from '$models';
import { useTranslation } from '$hooks';
import { formatPrice } from '$lib/helpers';
import { Flex, Grid } from '$components/layouts';
import { mq } from '$lib/helpers';

type ReceiptSummmaryProps = {
    order?: IOrderConfirmationModel;
    shipping?: IShippingType;
};

export const ReceiptSummary: FC<ReceiptSummmaryProps> = ({ order, shipping }: ReceiptSummmaryProps): JSX.Element => {
    const { translate } = useTranslation();
    const [freeShipping, setFreeShipping] = useState(false);

    useEffect(() => {
        if (shipping?.shippingPriceLimit) {
            const remaining = shipping?.shippingPriceLimit - (order?.totalIncVAT || 0);
            setFreeShipping(!remaining);
        }
    }, [order, shipping]);

    const calculateShipping = () => {
        if (order?.shippingDiscount === 0) {
            return formatPrice(0 + (shipping?.calculatedShippingPrice || 0));
        }
        if ((order?.shippingDiscount || 0) > 0) {
            return freeShipping
                ? formatPrice(0)
                : formatPrice(0 + (shipping?.calculatedShippingPrice || 0) + (order?.shippingDiscount || 0));
        }
        return formatPrice(0);
    };

    return (
        <SummmaryContainer column spacingBottom="medium">
            <TopSummary flow="row" rowGap="extrasmall" columns="1fr 1fr">
                {/* Total */}
                <div>{translate('checkout.orderSummary.totalPriceLabelExDiscount')}</div>
                <Price>{formatPrice(order?.totalExDiscount)}</Price>
                {/* Shipping */}
                {!order?.discount && !!order?.orderLines && (
                    <>
                        <div>
                            {shipping?.name
                                ? `${translate('checkout.orderSummary.deliveryPrefixLabel')} (${shipping.name})`
                                : translate('checkout.orderSummary.deliveryPrefixLabel')}
                        </div>
                        <Price>{calculateShipping()}</Price>
                    </>
                )}
                {/* Voucher shipping */}
                {!order?.discount && (order?.shippingDiscount || 0) > 0 && !!order?.voucherCode && !!order?.orderLines && (
                    <>
                        <Discount>{` - ${translate('checkout.orderSummary.voucherLabel')}: ${
                            order.voucherCode
                        }`}</Discount>
                        <Price discount>{freeShipping ? formatPrice(0) : formatPrice(order.shippingDiscount)}</Price>
                    </>
                )}
                {/* Normal discount */}
                {!!order?.discount && (
                    <>
                        <Discount>{translate('checkout.orderSummary.discountLabel')}</Discount>
                        <Price discount>{formatPrice(order.discount)}</Price>
                    </>
                )}
                {/* {!!order?.discount && order.discount - (order.voucherDiscount || 0) > 0 && (
                    <>
                        <Discount>{translate('checkout.orderSummary.discountLabel')}</Discount>
                        <Price discount>{formatPrice(order.discount - (order.voucherDiscount || 0))}</Price>
                    </>
                )} */}
                {!!order?.voucherCode && !!order.discount && (
                    <>
                        <div>
                            {shipping?.name
                                ? `${translate('checkout.orderSummary.deliveryPrefixLabel')} (${shipping.name})`
                                : translate('checkout.orderSummary.deliveryPrefixLabel')}
                        </div>
                        <Price>{calculateShipping()}</Price>
                    </>
                )}
                {/* Voucher discount shipping */}
                {(order?.shippingDiscount || 0) > 0 && order?.discount && order.voucherCode && (
                    <>
                        <Discount>{` - ${translate('checkout.orderSummary.voucherLabel')}: ${
                            order.voucherCode
                        }`}</Discount>
                        <Price discount>{freeShipping ? formatPrice(0) : formatPrice(order.shippingDiscount)}</Price>
                    </>
                )}
                {/* Vat */}
                <>
                    <div>{translate('checkout.orderSummary.vatLabel')}</div>
                    <Price>{formatPrice(order?.calculatedTotalVAT)}</Price>
                </>
            </TopSummary>
            <Grid flow="row" rowGap="extrasmall" columns="1fr 1fr">
                <PriceLabel>{`${translate('checkout.orderSummary.totalPriceInclVATLabel')} ${translate(
                    'checkout.orderSummary.inclVATLabel'
                )}`}</PriceLabel>
                <Price bold>{formatPrice(order?.totalIncVAT)}</Price>
            </Grid>
        </SummmaryContainer>
    );
};

const SummmaryContainer = styled(Flex)({
    width: '100%',

    [mq('md')]: {
        width: '50%',
    },
});

const TopSummary = styled(Grid)(({ theme }) => ({
    paddingBottom: theme.space[2],
    marginBottom: theme.space[3],
    borderBottom: theme.general.border,
    rowGap: theme.space[2],
}));

const PriceLabel = styled.div(({ theme }) => ({
    fontWeight: theme.fontWeights.bold,
}));

const Price = styled.div<{ discount?: boolean; bold?: boolean }>(({ theme, discount, bold }) => ({
    justifySelf: 'flex-end',
    color: discount ? theme.colors.primaryTheme : theme.colors.black,
    fontWeight: bold ? theme.fontWeights.bold : theme.fontWeights.regular,
}));

const Discount = styled.div(({ theme }) => ({
    color: theme.colors.primaryTheme,
}));
